import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GeoJSONGeometry } from '../../services/api';
import type { ContributionSheetSnap } from '../../components/map/mapSheetOffset';
import { isMultiCarriageway, parseOptionalWidthM } from '../../components/contributions/roadFlowUtils';
import type { DividedCarriagewayGuideSide } from '../../utils/dividedCarriagewayGeometry';
import { buildTraceGeometry } from '../../utils/contributionGeometry';
import {
  computeRoadGeometryStatusRows,
  geometryStatusLabel,
  nextRoadMarkingTarget,
  validateRoadCapturePlan,
  type RoadMarkingTarget,
} from '../../utils/roadContributionFlow';
import {
  dividedNeedsDivideTrace,
  parseDivideWidthM,
  resolveDividedStripCentres,
  stripNeedsMapTrace,
  type StripCaptureMode,
} from '../../utils/roadStripSurvey';

export type GeometryMode = 'point' | 'draw' | 'trace' | 'coordinates' | 'link' | 'enrich';

export interface RoadMarkingSessionDeps {
  isOpen: boolean;
  motorRoadSurveyFlow: boolean;
  roadCarriagewayCount: number | null;
  setRoadCarriagewayCount: (count: number) => void;
  roadWidth: string;
  roadCarriagewayAWidth: string;
  roadCarriagewayBWidth: string;
  setRoadLaneDirections: (v: []) => void;
  setRoadManualLaneDirections: (v: boolean) => void;
  setRoadCarriagewayALaneDirections: (v: []) => void;
  setRoadCarriagewayBLaneDirections: (v: []) => void;
  geometryMode: GeometryMode | null;
  drawnGeometry: GeoJSON.Geometry | null | undefined;
  tracePoints: Array<{ lat: number; lng: number }>;
  traceReady: boolean;
  markingGeometryFeatureType: string | null | undefined;
  isTracing: boolean;
  setStep: (step: string) => void;
  setSheetSnap: (snap: ContributionSheetSnap) => void;
  setGeometryMode: (mode: GeometryMode) => void;
  setSubmitFeedback: (v: { type: 'error' | 'success'; message: string } | null) => void;
  enterMarking: (mode: 'draw' | 'trace') => void;
  leaveMarking: () => void;
  requestAddressLookup: () => void;
  onPreviewGuideGeometries?: (
    guides: Array<{ side: DividedCarriagewayGuideSide; geometry: GeoJSON.LineString }> | null,
  ) => void;
  onPreviewGeometry?: (geometry: GeoJSONGeometry | null) => void;
  onClearGeometry: (opts?: { keepTrace?: boolean }) => void;
  onDrawTrash?: () => void;
  onEnableDrawing: (enabled: boolean) => void;
  onStopTrace: () => void;
  campaignTraceAutoStartRef: React.MutableRefObject<boolean>;
  resolveContributionLineGeometry: () => GeoJSON.LineString | null;
  onHydrateMarkingGeometry?: () => void;
}

/** Strip / divide marking + guided motor-road geometry flow. */
export function useRoadMarkingSession(deps: RoadMarkingSessionDeps) {
  const roadMarkingTargetRef = useRef<RoadMarkingTarget | null>(null);
  const stripReferenceAReadyRef = useRef(false);

  const [roadCapturePlanError, setRoadCapturePlanError] = useState<string | null>(null);
  const [singleStripCaptureMode, setSingleStripCaptureMode] =
    useState<StripCaptureMode>('trace_centerline');
  const [stripACaptureMode, setStripACaptureMode] = useState<StripCaptureMode>('derive_from_divide');
  const [stripBCaptureMode, setStripBCaptureMode] = useState<StripCaptureMode>('derive_from_divide');
  const [divideWidthM, setDivideWidthM] = useState('');
  const [dividedDivideLine, setDividedDivideLine] = useState<GeoJSON.LineString | null>(null);
  const [stripReferenceA, setStripReferenceA] = useState<GeoJSON.LineString | null>(null);
  const [stripReferenceB, setStripReferenceB] = useState<GeoJSON.LineString | null>(null);
  const [singleStripReference, setSingleStripReference] = useState<GeoJSON.LineString | null>(null);
  const [awaitingCarriagewayB, setAwaitingCarriagewayB] = useState(false);
  const [dividedSurveySetupHint, setDividedSurveySetupHint] = useState(false);

  const {
    isOpen,
    motorRoadSurveyFlow,
    roadCarriagewayCount,
    setRoadCarriagewayCount,
    roadWidth,
    roadCarriagewayAWidth,
    roadCarriagewayBWidth,
    setRoadLaneDirections,
    setRoadManualLaneDirections,
    setRoadCarriagewayALaneDirections,
    setRoadCarriagewayBLaneDirections,
    geometryMode,
    isTracing,
    setStep,
    setSheetSnap,
    setGeometryMode,
    setSubmitFeedback,
    enterMarking,
    leaveMarking,
    requestAddressLookup,
    onPreviewGuideGeometries,
    onPreviewGeometry,
    onClearGeometry,
    onDrawTrash,
    onEnableDrawing,
    onStopTrace,
    campaignTraceAutoStartRef,
    resolveContributionLineGeometry,
    onHydrateMarkingGeometry,
  } = deps;

  const resetMarkingSession = useCallback(() => {
    roadMarkingTargetRef.current = null;
    setSingleStripCaptureMode('trace_centerline');
    setStripACaptureMode('derive_from_divide');
    setStripBCaptureMode('derive_from_divide');
    setDivideWidthM('');
    setDividedDivideLine(null);
    setStripReferenceA(null);
    setStripReferenceB(null);
    setSingleStripReference(null);
    setAwaitingCarriagewayB(false);
    setDividedSurveySetupHint(false);
    setRoadCapturePlanError(null);
  }, []);

  const dividedStripWidthA = parseOptionalWidthM(roadCarriagewayAWidth) ?? parseOptionalWidthM(roadWidth);
  const dividedStripWidthB = parseOptionalWidthM(roadCarriagewayBWidth) ?? parseOptionalWidthM(roadWidth);

  const dividedStripCentres = useMemo(() => {
    if (!isMultiCarriageway(roadCarriagewayCount)) {
      return { centreA: null as GeoJSON.LineString | null, centreB: null as GeoJSON.LineString | null };
    }
    const needsDivide = dividedNeedsDivideTrace(stripACaptureMode, stripBCaptureMode);
    if (needsDivide && !dividedDivideLine) {
      return { centreA: null, centreB: null };
    }
    return resolveDividedStripCentres({
      divide: dividedDivideLine,
      divideWidthM: parseDivideWidthM(divideWidthM),
      widthA: dividedStripWidthA,
      widthB: dividedStripWidthB,
      modeA: stripACaptureMode,
      modeB: stripBCaptureMode,
      traceA: stripReferenceA,
      traceB: stripReferenceB,
    });
  }, [
    roadCarriagewayCount,
    dividedDivideLine,
    dividedStripWidthA,
    dividedStripWidthB,
    divideWidthM,
    stripACaptureMode,
    stripBCaptureMode,
    stripReferenceA,
    stripReferenceB,
  ]);

  const dividedCarriagewayBGeom = isMultiCarriageway(roadCarriagewayCount)
    ? dividedStripCentres.centreB
    : null;

  const stripATraceReady =
    stripACaptureMode === 'derive_from_divide' || !!stripReferenceA;
  const stripBTraceReady =
    stripBCaptureMode === 'derive_from_divide' || !!stripReferenceB;

  useEffect(() => {
    stripReferenceAReadyRef.current =
      stripACaptureMode === 'derive_from_divide' || !!stripReferenceA;
  }, [stripACaptureMode, stripReferenceA]);

  const dividedCarriagewayAReady = isMultiCarriageway(roadCarriagewayCount)
    ? stripACaptureMode === 'derive_from_divide'
      ? !!(dividedDivideLine && dividedStripWidthA != null && dividedStripCentres.centreA)
      : stripNeedsMapTrace(stripACaptureMode)
        ? !!stripReferenceA
        : false
    : false;

  const dividedCarriagewayBReady = isMultiCarriageway(roadCarriagewayCount)
    ? stripBCaptureMode === 'derive_from_divide'
      ? !!(dividedDivideLine && dividedStripWidthB != null && dividedStripCentres.centreB)
      : stripNeedsMapTrace(stripBCaptureMode)
        ? !!stripReferenceB
        : false
    : false;

  const dividedCarriagewayReady = isMultiCarriageway(roadCarriagewayCount)
    ? !!(dividedStripCentres.centreA && dividedStripCentres.centreB)
    : false;

  const singleStripTraceReady =
    roadCarriagewayCount === 1
      ? singleStripCaptureMode === 'derive_from_divide'
        ? false
        : !!(singleStripReference || resolveContributionLineGeometry())
      : false;

  const roadMarkingQueueParams = useMemo(
    () => ({
      carriagewayCount: roadCarriagewayCount,
      singleStripCaptureMode,
      stripACaptureMode,
      stripBCaptureMode,
      hasDivideLine: !!dividedDivideLine,
      hasStripReferenceA: !!stripReferenceA,
      hasStripReferenceB: !!stripReferenceB,
      hasSingleStripReference: !!singleStripReference,
    }),
    [
      roadCarriagewayCount,
      singleStripCaptureMode,
      stripACaptureMode,
      stripBCaptureMode,
      dividedDivideLine,
      stripReferenceA,
      stripReferenceB,
      singleStripReference,
    ],
  );

  const roadGeometryStatusRows = useMemo(
    () =>
      computeRoadGeometryStatusRows({
        carriagewayCount: roadCarriagewayCount,
        singleStripCaptureMode,
        stripACaptureMode,
        stripBCaptureMode,
        dividedDivideReady: !!dividedDivideLine,
        dividedCarriagewayAReady,
        dividedCarriagewayBReady,
        singleStripTraceReady,
      }).map((row) => ({
        label: row.label,
        statusLabel: geometryStatusLabel(row.status),
      })),
    [
      roadCarriagewayCount,
      singleStripCaptureMode,
      stripACaptureMode,
      stripBCaptureMode,
      dividedDivideLine,
      dividedCarriagewayAReady,
      dividedCarriagewayBReady,
      singleStripTraceReady,
    ],
  );

  const handleStripACaptureModeChange = useCallback((mode: StripCaptureMode) => {
    setStripACaptureMode(mode);
    if (mode === 'derive_from_divide') setStripReferenceA(null);
  }, []);

  const handleStripBCaptureModeChange = useCallback((mode: StripCaptureMode) => {
    setStripBCaptureMode(mode);
    if (mode === 'derive_from_divide') setStripReferenceB(null);
  }, []);

  const handleSingleStripCaptureModeChange = useCallback((mode: StripCaptureMode) => {
    setSingleStripCaptureMode(mode);
    if (mode === 'derive_from_divide') setSingleStripReference(null);
  }, []);

  const previewDividedGuideGeometries = useCallback(
    (markingTarget: RoadMarkingTarget | null) => {
      const guides: Array<{ side: DividedCarriagewayGuideSide; geometry: GeoJSON.LineString }> = [];
      if (!isMultiCarriageway(roadCarriagewayCount)) {
        onPreviewGuideGeometries?.(null);
        return;
      }
      if (dividedDivideLine) guides.push({ side: 'divide', geometry: dividedDivideLine });
      if (markingTarget !== 'strip_a') {
        const geomA = dividedStripCentres.centreA ?? stripReferenceA;
        if (geomA) guides.push({ side: 'a', geometry: geomA });
      }
      if (markingTarget !== 'strip_b' && !awaitingCarriagewayB) {
        const geomB = dividedStripCentres.centreB ?? stripReferenceB;
        if (geomB) guides.push({ side: 'b', geometry: geomB });
      }
      onPreviewGuideGeometries?.(guides.length > 0 ? guides : null);
    },
    [
      roadCarriagewayCount,
      dividedDivideLine,
      dividedStripCentres,
      stripReferenceA,
      stripReferenceB,
      awaitingCarriagewayB,
      onPreviewGuideGeometries,
    ],
  );

  const beginRoadStripMarking = useCallback(
    (
      target: RoadMarkingTarget,
      opts?: { assumeStripAReady?: boolean; preserveExisting?: boolean },
    ) => {
      const preserveExisting = opts?.preserveExisting === true;
      if (target === 'single_strip') {
        const mode = geometryMode === 'draw' || geometryMode === 'trace' ? geometryMode : 'trace';
        roadMarkingTargetRef.current = target;
        setAwaitingCarriagewayB(false);
        onPreviewGuideGeometries?.(null);
        if (isTracing) onStopTrace();
        setSubmitFeedback(null);
        setSheetSnap('compact');
        setGeometryMode(mode);
        setStep('marking');
        if (mode === 'draw') {
          if (!preserveExisting) {
            onDrawTrash?.();
            onClearGeometry();
            onPreviewGeometry?.(null);
            onEnableDrawing(false);
            window.setTimeout(() => enterMarking('draw'), 0);
            return;
          }
          enterMarking('draw');
          if (preserveExisting) {
            window.setTimeout(() => onHydrateMarkingGeometry?.(), 60);
          }
          return;
        }
        if (!preserveExisting) {
          onClearGeometry();
          onPreviewGeometry?.(null);
        }
        campaignTraceAutoStartRef.current = false;
        enterMarking('trace');
        if (preserveExisting) {
          window.setTimeout(() => onHydrateMarkingGeometry?.(), 0);
        }
        return;
      }

      if (target === 'strip_a' && stripACaptureMode === 'derive_from_divide') return;
      if (target === 'strip_b') {
        if (stripBCaptureMode === 'derive_from_divide') return;
        const stripAReady =
          stripATraceReady
          || opts?.assumeStripAReady
          || stripReferenceAReadyRef.current;
        if (!stripAReady) {
          setSubmitFeedback({
            type: 'error',
            message: 'Complete carriageway A first, then trace carriageway B.',
          });
          return;
        }
      }

      const mode = geometryMode === 'draw' || geometryMode === 'trace' ? geometryMode : 'trace';
      roadMarkingTargetRef.current = target;
      setAwaitingCarriagewayB(target === 'strip_b');
      previewDividedGuideGeometries(target);
      if (isTracing) onStopTrace();
      setSubmitFeedback(null);
      setSheetSnap('compact');
      setGeometryMode(mode);
      setStep('marking');

      if (mode === 'draw') {
        if (!preserveExisting) {
          onDrawTrash?.();
          onClearGeometry();
          onPreviewGeometry?.(null);
          onEnableDrawing(false);
          window.setTimeout(() => enterMarking('draw'), 0);
          return;
        }
        enterMarking('draw');
        if (preserveExisting) {
          window.setTimeout(() => onHydrateMarkingGeometry?.(), 60);
        }
        return;
      }

      if (!preserveExisting) {
        onClearGeometry();
        onPreviewGeometry?.(null);
      }
      campaignTraceAutoStartRef.current = false;
      enterMarking('trace');
      if (preserveExisting) {
        window.setTimeout(() => onHydrateMarkingGeometry?.(), 0);
      }
    },
    [
      stripACaptureMode,
      stripBCaptureMode,
      stripATraceReady,
      geometryMode,
      previewDividedGuideGeometries,
      isTracing,
      onStopTrace,
      onClearGeometry,
      onPreviewGeometry,
      onEnableDrawing,
      onDrawTrash,
      onPreviewGuideGeometries,
      setSubmitFeedback,
      setSheetSnap,
      setGeometryMode,
      setStep,
      enterMarking,
      campaignTraceAutoStartRef,
      onHydrateMarkingGeometry,
    ],
  );

  const startGuidedRoadMarking = useCallback(() => {
    const next = nextRoadMarkingTarget(roadMarkingQueueParams);
    if (next) {
      beginRoadStripMarking(next);
      return;
    }
    setSheetSnap('half');
    setStep('details');
  }, [roadMarkingQueueParams, beginRoadStripMarking, setSheetSnap, setStep]);

  const handleStartMarkingDivide = useCallback(() => beginRoadStripMarking('divide'), [beginRoadStripMarking]);
  const handleStartMarkingStripA = useCallback(() => beginRoadStripMarking('strip_a'), [beginRoadStripMarking]);
  const handleStartMarkingStripB = useCallback(() => beginRoadStripMarking('strip_b'), [beginRoadStripMarking]);
  const handleStartMarkingSingleStrip = useCallback(
    () => beginRoadStripMarking('single_strip'),
    [beginRoadStripMarking],
  );

  const handleEditRoadGeometry = useCallback(() => {
    const next = nextRoadMarkingTarget(roadMarkingQueueParams);
    if (next) {
      beginRoadStripMarking(next);
      return;
    }
    const preserveExisting = true;
    if (roadCarriagewayCount === 1) {
      beginRoadStripMarking('single_strip', { preserveExisting });
    } else if (isMultiCarriageway(roadCarriagewayCount)) {
      beginRoadStripMarking(dividedDivideLine ? 'strip_a' : 'divide', { preserveExisting });
    }
  }, [
    roadMarkingQueueParams,
    beginRoadStripMarking,
    roadCarriagewayCount,
    dividedDivideLine,
  ]);

  const handleRoadLayoutSelect = useCallback(
    (count: 1 | 2) => {
      setRoadCapturePlanError(null);
      roadMarkingTargetRef.current = null;
      onPreviewGuideGeometries?.(null);
      onPreviewGeometry?.(null);
      if (isTracing) onStopTrace();
      onClearGeometry({ keepTrace: false });
      onDrawTrash?.();
      onEnableDrawing(false);

      setRoadCarriagewayCount(count);
      setRoadLaneDirections([]);
      setRoadManualLaneDirections(false);
      setRoadCarriagewayALaneDirections([]);
      setRoadCarriagewayBLaneDirections([]);
      setDividedDivideLine(null);
      setStripReferenceA(null);
      setStripReferenceB(null);
      setSingleStripReference(null);
      setAwaitingCarriagewayB(false);
      setDividedSurveySetupHint(false);

      if (count === 1) {
        setSingleStripCaptureMode('trace_centerline');
      } else {
        setStripACaptureMode('derive_from_divide');
        setStripBCaptureMode('derive_from_divide');
      }
      setStep('mode');
    },
    [
      isTracing,
      onStopTrace,
      onClearGeometry,
      onDrawTrash,
      onEnableDrawing,
      onPreviewGuideGeometries,
      onPreviewGeometry,
      setRoadCarriagewayCount,
      setRoadLaneDirections,
      setRoadManualLaneDirections,
      setRoadCarriagewayALaneDirections,
      setRoadCarriagewayBLaneDirections,
      setStep,
    ],
  );

  const handleRoadCapturePlanContinue = useCallback(() => {
    if (roadCarriagewayCount == null) return;
    const err = validateRoadCapturePlan({
      carriagewayCount: roadCarriagewayCount,
      singleStripCaptureMode,
      stripACaptureMode,
      stripBCaptureMode,
    });
    if (err) {
      setRoadCapturePlanError(err);
      return;
    }
    setRoadCapturePlanError(null);
    startGuidedRoadMarking();
  }, [
    roadCarriagewayCount,
    singleStripCaptureMode,
    stripACaptureMode,
    stripBCaptureMode,
    startGuidedRoadMarking,
  ]);

  const handleRoadCarriagewayCountChange = useCallback(
    (count: number) => {
      setRoadCarriagewayCount(count);
      roadMarkingTargetRef.current = null;
      if (!isMultiCarriageway(count)) {
        setDividedDivideLine(null);
        setStripReferenceA(null);
        setStripReferenceB(null);
        setAwaitingCarriagewayB(false);
        setDividedSurveySetupHint(false);
        onPreviewGuideGeometries?.(null);
        return;
      }

      const hadSingleLineCapture =
        !!singleStripReference
        || !!resolveContributionLineGeometry();
      setDividedSurveySetupHint(hadSingleLineCapture);
      setSingleStripReference(null);
      setDividedDivideLine(null);
      setStripReferenceA(null);
      setStripReferenceB(null);
      setStripACaptureMode('derive_from_divide');
      setStripBCaptureMode('derive_from_divide');
      setAwaitingCarriagewayB(false);
      onPreviewGuideGeometries?.(null);
      onPreviewGeometry?.(null);
      if (isTracing) onStopTrace();
      onClearGeometry({ keepTrace: false });
      onDrawTrash?.();
      onEnableDrawing(false);
      campaignTraceAutoStartRef.current = false;
    },
    [
      singleStripReference,
      resolveContributionLineGeometry,
      isTracing,
      onStopTrace,
      onClearGeometry,
      onDrawTrash,
      onEnableDrawing,
      onPreviewGuideGeometries,
      onPreviewGeometry,
      setRoadCarriagewayCount,
      campaignTraceAutoStartRef,
    ],
  );

  /** Returns true when capture advanced the guided queue (caller should not go to details yet). */
  const confirmRoadMarkingCapture = useCallback(
    (capturedLine: GeoJSON.LineString): boolean => {
      const markingTarget = roadMarkingTargetRef.current;
      if (!markingTarget) return false;

      if (markingTarget === 'strip_b') {
        setStripReferenceB(capturedLine);
      } else if (markingTarget === 'strip_a') {
        stripReferenceAReadyRef.current = true;
        setStripReferenceA(capturedLine);
      } else if (markingTarget === 'divide') {
        setDividedDivideLine(capturedLine);
        setDividedSurveySetupHint(false);
      } else if (markingTarget === 'single_strip') {
        setSingleStripReference(capturedLine);
      }
      roadMarkingTargetRef.current = null;
      setAwaitingCarriagewayB(false);
      leaveMarking();
      requestAddressLookup();

      if (motorRoadSurveyFlow) {
        const next = nextRoadMarkingTarget({
          ...roadMarkingQueueParams,
          hasDivideLine: markingTarget === 'divide' ? true : roadMarkingQueueParams.hasDivideLine,
          hasStripReferenceA: markingTarget === 'strip_a' ? true : roadMarkingQueueParams.hasStripReferenceA,
          hasStripReferenceB: markingTarget === 'strip_b' ? true : roadMarkingQueueParams.hasStripReferenceB,
          hasSingleStripReference:
            markingTarget === 'single_strip' ? true : roadMarkingQueueParams.hasSingleStripReference,
        });
        if (next) {
          beginRoadStripMarking(next, {
            assumeStripAReady: markingTarget === 'strip_a',
          });
          return true;
        }
      }
      setSheetSnap('half');
      setStep('details');
      return true;
    },
    [
      leaveMarking,
      requestAddressLookup,
      motorRoadSurveyFlow,
      roadMarkingQueueParams,
      beginRoadStripMarking,
      setSheetSnap,
      setStep,
    ],
  );

  useEffect(() => {
    if (!isOpen) {
      onPreviewGuideGeometries?.(null);
      return;
    }
    if (!isMultiCarriageway(roadCarriagewayCount)) {
      onPreviewGuideGeometries?.(null);
      return;
    }
    previewDividedGuideGeometries(roadMarkingTargetRef.current);
  }, [
    isOpen,
    roadCarriagewayCount,
    previewDividedGuideGeometries,
    onPreviewGuideGeometries,
    dividedDivideLine,
    dividedStripCentres,
    stripReferenceA,
    stripReferenceB,
    awaitingCarriagewayB,
  ]);

  return {
    roadMarkingTargetRef,
    roadCapturePlanError,
    setRoadCapturePlanError,
    singleStripCaptureMode,
    setSingleStripCaptureMode,
    stripACaptureMode,
    setStripACaptureMode,
    stripBCaptureMode,
    setStripBCaptureMode,
    divideWidthM,
    setDivideWidthM,
    dividedDivideLine,
    setDividedDivideLine,
    stripReferenceA,
    setStripReferenceA,
    stripReferenceB,
    setStripReferenceB,
    singleStripReference,
    setSingleStripReference,
    awaitingCarriagewayB,
    setAwaitingCarriagewayB,
    dividedSurveySetupHint,
    setDividedSurveySetupHint,
    dividedStripCentres,
    dividedCarriagewayBGeom,
    dividedCarriagewayCentreA: dividedStripCentres.centreA,
    stripATraceReady,
    stripBTraceReady,
    dividedCarriagewayAReady,
    dividedCarriagewayBReady,
    dividedCarriagewayReady,
    singleStripTraceReady,
    roadMarkingQueueParams,
    roadGeometryStatusRows,
    resetMarkingSession,
    handleStripACaptureModeChange,
    handleStripBCaptureModeChange,
    handleSingleStripCaptureModeChange,
    beginRoadStripMarking,
    startGuidedRoadMarking,
    handleStartMarkingDivide,
    handleStartMarkingStripA,
    handleStartMarkingStripB,
    handleStartMarkingSingleStrip,
    handleEditRoadGeometry,
    handleRoadLayoutSelect,
    handleRoadCapturePlanContinue,
    handleRoadCarriagewayCountChange,
    confirmRoadMarkingCapture,
  };
}

/** Resolve line geometry for contribution submit / marking confirm. */
export function resolveContributionLineFromSources(input: {
  geometryMode: GeometryMode | null;
  drawnGeometry: GeoJSON.Geometry | null | undefined;
  tracePoints: Array<{ lat: number; lng: number }>;
  traceReady: boolean;
  markingGeometryFeatureType: string | null | undefined;
}): GeoJSON.LineString | null {
  const { geometryMode, drawnGeometry, tracePoints, traceReady, markingGeometryFeatureType } = input;
  if (!geometryMode || !markingGeometryFeatureType) return null;
  if (geometryMode === 'draw' && drawnGeometry?.type === 'LineString') {
    return drawnGeometry as GeoJSON.LineString;
  }
  if (geometryMode === 'trace') {
    if (tracePoints.length >= 2) {
      const traced = buildTraceGeometry(tracePoints, markingGeometryFeatureType);
      if (traced?.type === 'LineString') {
        return traced as GeoJSON.LineString;
      }
    }
    if (traceReady) {
      const traced = buildTraceGeometry(tracePoints, markingGeometryFeatureType);
      if (traced?.type === 'LineString') {
        return traced as GeoJSON.LineString;
      }
    }
    if (drawnGeometry?.type === 'LineString') {
      return drawnGeometry as GeoJSON.LineString;
    }
  }
  return null;
}
